<template>
  <div>
    <b-row>
      <b-modal id="modal-assign" hide-footer ref="escalateSubmissions" title="Escalate Business Continuity">
        <Escalation :task-id="effectiveTrackingNumber" type="business-annual-returns" permission="can_view_business_annual_returns_escalations"/>
      </b-modal>
      <b-modal id="modal-confirm" hide-footer ref="confirm" title="Confirm">
        <b-row>
          <b-col>
            <p class="text-sm">
              <span class="h6">Are you sure you are ready to take this action?</span>.<br/>
              This action can only be reversed by your superiors. Only confirm if you are certain that this is the appropriate Final Decision
            </p>
            <p class="mt-3 text-sm italic">
              <b>Note:</b><br/>
              All actions performed on <b class="text-blue">URSB eRegistry</b> are recorded and details of the account that initiated are tagged to the action.
            </p>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="4">
            <button class="px-3 py-2 border-2 border-blue bg-white rounded-sm text-blue w-full" @click="cancelConfirmation">
              <i class="fa fa-minus-square text-blue mr-2"></i>Cancel</button>
          </b-col>
          <b-col cols="4"></b-col>
          <b-col cols="4">
            <button class="px-3 py-2 bg-green rounded-sm text-white w-full hover:bg-green-dark" @click="yesConfirmation">
              <i class="fa fa-check-circle text-white mr-1"></i> Accept</button>
          </b-col>
        </b-row>
      </b-modal>
      <b-modal id="modal-query" hide-footer ref="query-incorporation" title="Query Submission">
        <b-col cols="12">
          <b-form class="w-full">
            <b-form-textarea
              v-model="form.reason"
              type="text"
              placeholder="Give description of query"
              class="mt-1"
              required>
            </b-form-textarea>
            <p v-if="form.reason" class="my-2 text-xs">{{form.reason.length}}/{{maxText}}</p>
            <b-row>
              <b-col cols="4">
                <b-button class="btn-query-hover" variant="primary" @click="onQuery">
                  Query
                  <b-spinner small v-if="loading"></b-spinner>
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-col>
      </b-modal>
    </b-row>
    <b-row class="my-1 mx-1">
      <b-col>
        <b-overlay :show="detailsLoading" rounded="sm" opacity="0.75">
        <b-row class="mt-1">
          <b-col>
            <span style="font-size: 30px; font-weight: bold" v-if="hasDetails">
              {{detailsForm.name || detailsForm.companyName}}
            </span>
          </b-col>
        </b-row>
        <b-row class="mt-1" v-if="hasDetails">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2">Application Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Tracking Number</th>
                <th class="px-2 py-1">Status</th>
                <th class="px-2 py-1">Years Filing</th>
                <th class="px-2 py-1">Submission Date</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" >
                <td class="px-2 py-1">
                  <span class="text-gray">
                    {{effectiveTrackingNumber}}
                  </span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray text-capitalize">{{detailsStatus}}</span>
                </td>
                <td class="px-2 py-1">
                  <div class="years-wrap">
                    <b-badge
                      v-for="(year, idx) in yearsFiling"
                      :key="`${year}-${idx}`"
                      class="year-chip"
                    >
                      {{ year }}
                    </b-badge>
                    <span v-if="yearsFiling.length === 0" class="text-gray">-</span>
                  </div>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray italic">
                    {{dateTimeFormat(detailsForm.createdAt)}}
                  </span>
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
        <b-row class="mt-2" v-if="hasDetails && hasPaymentDetails">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2">Payment Details</td>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;">
                <td class="px-2 py-1 font-bold">PRN</td>
                <td class="px-2 py-1">{{ paymentPrn || "-" }}</td>
              </tr>
              <tr v-if="paymentStatus" style="border: solid 1px #dedede;">
                <td class="px-2 py-1 font-bold">Status</td>
                <td class="px-2 py-1 text-capitalize">{{ String(paymentStatus).toLowerCase().replaceAll("_", " ") }}</td>
              </tr>
              </tbody>
            </table>
            <div class="mt-2" v-if="paymentPrn">
              <PRNViewer :tracking-number="effectiveTrackingNumber" :prn="paymentPrn"/>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-2" v-else-if="hasDetails">
          <b-col cols="12">
            <PRNViewer :tracking-number="effectiveTrackingNumber"/>
          </b-col>
        </b-row>
        <b-row class="my-3" v-if="hasDetails">
          <b-col cols="12" md="4" lg="6"></b-col>
          <b-col cols="12" md="8" lg="6">
            <div class="flex float-end">
              <button class="btn-escalate" @click="escalatePrompt">
                <i class="fa fa-arrow-up text-blue mr-1"></i>Escalate
              </button>
              <button type="button" class="btn-query" v-b-modal.modal-query>
                <i class="fa fa-question text-white mr-1"></i> Query
              </button>
              <button type="button" class="btn-approve" @click="onApprove">
                <i class="fa fa-check-circle text-white mr-1"></i> Approve
              </button>
            </div>
          </b-col>
        </b-row>
        <b-row class="my-2">
          <b-col>
            <b-row>
              <b-col>
                <div class="flex h-full" style="cursor: pointer;">
                  <div :class="[(currentRoute === 'forms') ? 'menu-item-active' : 'menu-item']" @click="select('forms')">
                    <div class="flex mt-6">
                      <div style="font-size: 14px">Filing Information</div>
                    </div>
                  </div>
                  <div :class="[(currentRoute === 'attachments') ? 'menu-item-active' : 'menu-item']" @click="select('attachments')">
                    <div class="flex mt-6">
                      <div style="font-size: 14px">Attachments</div>
                    </div>
                  </div>
                  <div :class="[(currentRoute === 'messages') ? 'menu-item-active' : 'menu-item']" @click="select('messages')">
                    <div class="flex mt-6">
                      <div style="font-size: 14px">Messages</div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <BusinessAnnualReturnForms
                  :key="`forms-${effectiveTrackingNumber || 'none'}`"
                  :task="detailsForm"
                  :tracking-number="effectiveTrackingNumber"
                  v-if="currentRoute === 'forms' && hasDetails"
                />
                <BusinessAnnualReturnFormFiles
                  :key="`attachments-${effectiveTrackingNumber || 'none'}`"
                  :task="detailsForm"
                  :tracking-number="effectiveTrackingNumber"
                  v-if="currentRoute === 'attachments' && hasDetails"
                />

                <BusinessAnnualReturnMessages
                  :tracking-number="effectiveTrackingNumber"
                  v-if="currentRoute === 'messages' && hasDetails"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        </b-overlay>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Escalation from "@/views/home/commons/Escalation.vue";
import PRNViewer from "@/views/home/commons/PRNViewer.vue";
import BusinessAnnualReturnForms
    from "@/views/home/services/business-annual-returns/task/BusinessAnnualReturnForms.vue";
import BusinessAnnualReturnFormFiles
    from "@/views/home/services/business-annual-returns/task/BusinessAnnualReturnFormFiles.vue";
import BusinessAnnualReturnMessages
    from "@/views/home/services/business-annual-returns/task/BusinessAnnualReturnMessages.vue";

export default {
  name: "BusinessAnnualReturnsTask",
  components: {
      BusinessAnnualReturnMessages,
      BusinessAnnualReturnFormFiles, BusinessAnnualReturnForms,  PRNViewer, Escalation },
  props:{task:Object},
  data(){
    return{
      margin:"2px",
      currentRoute: "forms",
      loading:false,
      detailsLoading:false,
      messages:[],
      taskId:null,
      maxText:500,
      snapshot:null,
      snapshots:[],
      form:{
        trackingNumber:null,
        reason:null,
        status:null,
      },
    }
  },
  mounted() {
    this.loadDetails()
  },
  methods:{
    ...mapActions(["updateBusinessAnnualReturnsStatus", "fetchBusinessAnnualReturnsTask", "fetchBusinessAnnualReturnsDetails"]),
    loadDetails(){
      const trackingNo = this.effectiveTrackingNumber
      if(!trackingNo){
        this.detailsLoading = false
        return
      }
      this.detailsLoading = true
      this.fetchBusinessAnnualReturnsDetails(trackingNo)
    },
    onApprove(){
      this.form.trackingNumber = this.taskTrackingNumber
      this.form.status = "APPROVED"
      this.action = "final"
      this.$refs.confirm.show();
    },
    onQuery(){
      this.form.trackingNumber = this.taskTrackingNumber
      this.form.status = "QUERIED"
      this.action = "final"
      if(this.form.reason != null && String(this.form.reason).trim() !== ""){
        this.$refs.confirm.show();
      }else{
        this.errorToast("Missing Information", "Provide a message for this final decision")
      }
    },
    buildStatusPayload(){
      const trimmedReason = this.form.reason != null ? String(this.form.reason).trim() : ""
      const safeReason =
        this.form.status === "APPROVED" && trimmedReason === ""
          ? "a"
          : trimmedReason

      return {
        trackingNumber: this.form.trackingNumber || this.taskTrackingNumber,
        status: this.form.status,
        reason: safeReason,
      }
    },
    select(route){
      this.currentRoute = route
      if((route === "forms" || route === "attachments") && this.effectiveTrackingNumber){
        this.detailsLoading = true
        this.fetchBusinessAnnualReturnsDetails(this.effectiveTrackingNumber)
      }
    },
    yesConfirmation(){
      if(this.action === "final"){
        this.loading = true
        this.updateBusinessAnnualReturnsStatus(this.buildStatusPayload())
      }

      if(this.action === "escalate"){
        this.$refs.escalateSubmissions.show();
      }
      this.cancelConfirmation()
    },
    cancelConfirmation(){
      this.action = null
      this.$refs.confirm.hide();
    },
    escalatePrompt(){
      this.action = "escalate"
      this.$refs.confirm.show();
    }
  },
  computed:{
    ...mapGetters([
      "getBusinessAnnualReturnsTask",
      "getBusinessAnnualReturnsDetails",
      "getBusinessAnnualReturnsDetailsError",
      "getBusinessAnnualReturnsUpdateStatusSuccess",
      "getBusinessAnnualReturnsUpdateStatusSuccessError"
    ]),
    taskTrackingNumber(){
      const data = this.task && this.task.data ? this.task.data : {};
      return data.trackingNo || data.trackingNumber || null;
    },
    effectiveTrackingNumber(){
      return this.taskTrackingNumber
        || this.detailsForm.trackingNumber
        || this.detailsForm.trackingNo
        || null
    },
    hasDetails(){
      return Boolean(this.effectiveTrackingNumber)
    },
    detailsStatus(){
      const status = this.detailsForm.status || (this.task && this.task.status)
      return typeof status === "string" ? status.toLowerCase() : ""
    },
    taskQueueId(){
      const data = this.task && this.task.data ? this.task.data : {};
      return this.task.taskId || data.taskId || this.task.id || null;
    },
    detailsForm(){
      const details = this.getBusinessAnnualReturnsDetails
      if(details && (details.trackingNumber || details.trackingNo)){
        return details
      }
      if(details && details.data && (details.data.trackingNumber || details.data.trackingNo)){
        return details.data
      }
      return {}
    },
    paymentPrn(){
      const d = this.detailsForm
      return d.prn || d.paymentPrn || (d.payment && d.payment.prn) || null
    },
    paymentStatus(){
      const d = this.detailsForm
      return d.paymentStatus || (d.payment && d.payment.status) || null
    },
    hasPaymentDetails(){
      return Boolean(this.paymentPrn || this.paymentStatus)
    },
    yearsFiling(){
      const raw = this.detailsForm.years != null
        ? this.detailsForm.years
        : (this.task && this.task.data ? this.task.data.years : null)
      if(Array.isArray(raw)){
        return raw.map((y) => String(y).trim()).filter(Boolean)
      }
      if(typeof raw === "string"){
        return raw
          .split(",")
          .map((y) => y.trim())
          .filter((y) => y.length > 0)
      }
      if(raw == null){
        return []
      }
      return [String(raw).trim()].filter(Boolean)
    }
  },
  watch:{
    taskTrackingNumber(){
      this.loadDetails()
    },
    getBusinessAnnualReturnsDetails(){
      this.detailsLoading = false
    },
    getBusinessAnnualReturnsDetailsError(data){
      this.detailsLoading = false
      this.errorToast("Error", data?.message ?? data?.data?.message ?? "Failed to load application details")
    },
    getBusinessAnnualReturnsUpdateStatusSuccess(){
      this.loading = false
      this.successToast("Success!", "Final Decision Successful")
      window.location.reload()
    },
    getBusinessAnnualReturnsUpdateStatusSuccessError(data){
      this.loading = false
      this.errorToast("Error!", data.message)
    }
  }
};
</script>

<style scoped>

.icon{
  width: 170px;
}

th, td{
  font-size: 12px;
}

.years-wrap{
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.year-chip{
  background: #eef2ff;
  color: #1e40af;
  font-weight: 600;
}

.btn-escalate{
  @apply border border-blue rounded-sm text-blue text-sm px-3 py-1 mx-1 bg-white;
}

.btn-escalate:hover{
  @apply text-blue-dark;
}

.btn-approve{
  @apply border border-blue bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1;
}

.btn-query{
  @apply border border-blue bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1;
}

.btn-approve:hover{
  @apply bg-green border-green text-white;
}

.btn-query:hover{
  @apply bg-orange border-orange text-white;
}

.btn-cancel:hover{
  @apply bg-red border-red text-white;
}

.btn-approve-hover{
  @apply bg-green border-green text-white mt-2 px-4 py-1 rounded-sm;
}

.btn-query-hover{
  @apply bg-orange border-orange text-white mt-2 px-4 py-1 rounded-sm;
}

.btn-approve-hover:hover{
  @apply bg-green-dark;
}

.btn-query-hover:hover{
  @apply bg-orange-dark;
}

</style>